import React, { useState, useCallback, useMemo } from 'react';
import { type Locale } from '@i18next/ui';
import { useQuery } from "@apollo/client";
import Pagination from "@react/widget/Pagination";
import Provider from '@module/apollo/client/Provider';
import { GetNewsQuery, type GetNewsResponse, type GetNewsVariables } from '@graphql/news/GetNews';
import SolidArrowRightSvg from "@assets/icon/solid/arrow-right.svg";

interface NewsContentProps {
  siteHandle: Locale;
  limit: number;
  page: number;
  year: string;
  label?: Record<string, string>;
  getNewsByPage: (page: number) => void;
}
const NewsContent: React.FC<NewsContentProps> = ({ siteHandle, limit, page, year, label, getNewsByPage }) => {
  const offset = (page - 1) * limit;
  const currentYear = new Date().getFullYear();
  const postDate =
    year === "*"
      ? ["and", ">= 2008-01-01", `< ${currentYear + 1}-01-01`]
      : ["and", `>= ${year}-01-01`, `< ${+year + 1}-01-01`];

  
  const { loading, error, data } = useQuery<GetNewsResponse, GetNewsVariables>(GetNewsQuery, {
    variables: {
      site: siteHandle,
      limit,
      offset,
      postDate
    }
  });

  if (loading) return <p>{label?.load}...</p>;
  if (error) return <p>Error: {error.message}</p>;

  const total = Math.ceil((data?.entryCount || 0) / limit);
  if(!data || total <=0) 
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 border-t pt-12">
        <p>{label?.nodata}</p>
      </div>
    );
  return (
    <>
      <ul className="[&>li:not(:last-child)]:border-b border-t max-w-4xl mx-auto">
        {data.entries?.map(({ id, siteId, title, url, subtitle, featuredDescription }) => (
          <li key={`${siteId}-${id}`} className="py-7.5">
            <h3>{title}</h3>
            {!!featuredDescription ? (
              <p className="mt-2">
                <i>{featuredDescription}</i>
              </p>
            ) : (!!subtitle && <p className="mt-4.5" dangerouslySetInnerHTML={{ __html: subtitle }}/>)}
            <div className="mt-5">
              <a
                href={url}
                className="btn flex justify-center font-semibold transition duration-300 cursor-pointer [&>*]:align-middle items-center bg-white text hover:text-primary-hover active:bg-white [&_.icon]:text-primary group/btn w-full sm:w-fit px-5 md:px-6 py-1.5 md:py-2 text-base rounded-full border"
              >
                {label?.read}
                <svg
                  className="icon ml-5 flex-none btn-icon transition-all duration-300 group-hover/btn:translate-x-2"
                  viewBox="0 0 16 16"
                  width="16"
                  height="16"
                >
                  <path stroke="currentColor" strokeWidth="1.5" d="m8.735 3 5.53 5.53M8.735 13l5.53-5.53M1.735 8h12"></path>
                </svg>
              </a>
            </div>
          </li>
        ))}
      </ul>
      <Pagination
        label={{ prev: label?.prev || "Prev", next: label?.next || "Next" }}
        page={page}
        total={total}
        onChange={getNewsByPage}
      />
    </>
  );
};

interface Props {
  siteHandle: Locale;
  label: Record<string, string>;
}

const NewsList: React.FC<Props> = ({ siteHandle, label }) => {
  const [year, setYear] = useState<string>("*");
  const [page, setPage] = useState<number>(1);
  const limit = 9;

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 2014 + 1 }, (_, i) => `${currentYear - i}`);
  }, []);

  const handleYearChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(e.target.value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  return (
    <>
      <div className="mb-8 max-w-4xl mx-auto">
        <div className="w-full">
          <p className="text-lg mb-2 font-semibold">{label.yearLable}</p>
          <select
            className="block w-full mt-1 rounded-md border-gray-100 focus:border-gray-300"
            defaultValue={year}
            onChange={handleYearChange}
          >
            <option value="*">{label.allLable}</option>
            {years.map((_year) => (
              <option key={_year} value={_year}>
                {_year}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="max-w-4xl mx-auto">
        <Provider>
          <NewsContent
            label={label}
            siteHandle={siteHandle}
            limit={limit}
            page={page}
            year={year}
            getNewsByPage={handlePageChange}
          />
        </Provider>
      </div>
    </>
  );
};

export default NewsList;